import { create } from 'zustand';
import { FormStructure } from '@/features/application/types/forms';
import { logger } from '@/features/help/utils/logger';
import { toast } from 'react-hot-toast';
import {
  IncompatibleFieldMapping,
  detectIncompatibleMappingsForPrimaryObjectChange,
  clearIncompatibleMappings,
} from '../utils/field-mapping-validation.utils';

export interface TestResult {
  formFieldLabel: string;
  crmFieldLabel: string;
  isValid: boolean;
  error?: string;
}

export interface ValidationStore {
  // Test results state
  testResults: {
    isOpen: boolean;
    results: TestResult[];
    totalMappings: number;
    validMappings: number;
    inProgress: boolean;
    saveCompleted?: boolean;
  };

  // Primary object change warning state
  primaryObjectChangeWarning: {
    isOpen: boolean;
    currentPrimaryObject: string | null;
    newPrimaryObject: string | null;
    incompatibleMappings: IncompatibleFieldMapping[];
  };

  // Actions
  setTestResults: (results: Partial<ValidationStore['testResults']>) => void;
  closeTestResults: () => void;
  addTestResult: (result: TestResult) => void;

  // Primary object change with warnings
  requestPrimaryObjectChange: (
    newPrimaryObject: string,
    currentPrimaryObject: string | null,
    currentStructure: FormStructure | null,
    getAllowedObjectsForPrimaryObject: (primaryObject: string) => Set<string> | null,
    onConfirm: (newPrimaryObject: string) => void
  ) => void;

  confirmPrimaryObjectChange: (
    currentStructure: FormStructure | null,
    onUpdate: (updatedStructure: FormStructure | null) => void,
    onConfirm: (newPrimaryObject: string) => void
  ) => void;

  cancelPrimaryObjectChange: () => void;
}

export const useValidationStore = create<ValidationStore>((set, get) => ({
  testResults: {
    isOpen: false,
    results: [],
    totalMappings: 0,
    validMappings: 0,
    inProgress: false,
  },

  primaryObjectChangeWarning: {
    isOpen: false,
    currentPrimaryObject: null,
    newPrimaryObject: null,
    incompatibleMappings: [],
  },

  setTestResults: (resultsUpdate) =>
    set((state) => ({ testResults: { ...state.testResults, ...resultsUpdate } })),

  closeTestResults: () =>
    set((state) => ({ testResults: { ...state.testResults, isOpen: false } })),

  addTestResult: (result) =>
    set((state) => ({
      testResults: { ...state.testResults, results: [...state.testResults.results, result] },
    })),

  requestPrimaryObjectChange: (
    newPrimaryObject: string,
    currentPrimaryObject: string | null,
    currentStructure: FormStructure | null,
    getAllowedObjectsForPrimaryObject: (primaryObject: string) => Set<string> | null,
    onConfirm: (newPrimaryObject: string) => void
  ) => {
    // If no current primary object, just set the new one without warning
    if (!currentPrimaryObject) {
      onConfirm(newPrimaryObject);
      return;
    }

    // If it's the same object, no change needed
    if (currentPrimaryObject === newPrimaryObject) {
      return;
    }

    logger.info('ValidationStore', 'Requesting primary object change', {
      currentPrimaryObject,
      newPrimaryObject,
    });

    // Get allowed objects for the new primary object
    const newAllowedObjects = getAllowedObjectsForPrimaryObject(newPrimaryObject);
    if (!newAllowedObjects) {
      logger.warn('ValidationStore', 'Could not determine allowed objects for new primary object', {
        newPrimaryObject,
      });
      return;
    }

    // Detect incompatible mappings
    const incompatibleMappings = detectIncompatibleMappingsForPrimaryObjectChange(
      currentStructure,
      newPrimaryObject,
      newAllowedObjects
    );

    // If no incompatible mappings, proceed directly
    if (incompatibleMappings.length === 0) {
      onConfirm(newPrimaryObject);
      toast.success(`Primary object changed to ${newPrimaryObject}`);
      return;
    }

    // Show warning dialog
    set({
      primaryObjectChangeWarning: {
        isOpen: true,
        currentPrimaryObject,
        newPrimaryObject,
        incompatibleMappings,
      },
    });
  },

  confirmPrimaryObjectChange: (
    currentStructure: FormStructure | null,
    onUpdate: (updatedStructure: FormStructure | null) => void,
    onConfirm: (newPrimaryObject: string) => void
  ) => {
    const state = get();
    const warningState = state.primaryObjectChangeWarning;

    if (!warningState.newPrimaryObject) {
      logger.error('ValidationStore', 'No new primary object in warning state');
      return;
    }

    // Clear incompatible mappings
    const { updatedFormStructure } = clearIncompatibleMappings(
      currentStructure,
      warningState.incompatibleMappings
    );

    // Apply the changes
    onUpdate(updatedFormStructure);

    // Confirm the change
    onConfirm(warningState.newPrimaryObject);
    toast.success(
      `Primary object changed to ${warningState.newPrimaryObject}. Incompatible mappings were cleared.`
    );

    // Close the dialog
    set({
      primaryObjectChangeWarning: {
        isOpen: false,
        currentPrimaryObject: null,
        newPrimaryObject: null,
        incompatibleMappings: [],
      },
    });
  },

  cancelPrimaryObjectChange: () =>
    set({
      primaryObjectChangeWarning: {
        isOpen: false,
        currentPrimaryObject: null,
        newPrimaryObject: null,
        incompatibleMappings: [],
      },
    }),
}));
